/**
 * Mermaid diagram rendering utilities.
 * Matches the React reference's renderMermaidToImg approach.
 */
"use client";

import mermaid from "mermaid";
import type { MermaidConfig } from "mermaid";
import { Chart, registerables } from "chart.js";

Chart.register(...registerables);

// Mermaid configuration
const MERMAID_CONFIG: MermaidConfig = {
  startOnLoad: false,
  theme: "default",
  securityLevel: "loose",
  fontFamily: "var(--font-inter-tight), system-ui, sans-serif",
};

// Initialize mermaid once
let mermaidInitialized = false;
function ensureMermaidInit() {
  if (!mermaidInitialized) {
    mermaid.initialize(MERMAID_CONFIG);
    mermaidInitialized = true;
  }
}

/** Normalize mermaid source code. */
function normalizeMermaidSource(source: string): string {
  return source
    .replace(/\r\n/g, "\n")
    .replace(/\t/g, "  ")
    .trim();
}

/** Regex to detect unfenced mermaid diagram code. */
export const MERMAID_KEYWORD_RE =
  /^(graph|flowchart|sequenceDiagram|pie|gantt|classDiagram|stateDiagram|stateDiagram-v2|erDiagram|timeline|quadrantChart|requirementDiagram|block|packet|zenuml|sankey|kanban|architecture|radar|treemap|venn|treeView|xyChart)\b/im;

/**
 * Convert <foreignObject> elements in an SVG string to <text> elements.
 * Mermaid renders flowchart node labels inside <foreignObject> containers;
 * canvas drawImage and WeasyPrint silently drop them, losing all text.
 * This preserves text by extracting it and creating positioned <text> elements.
 */
export function convertForeignObjectToText(svg: string): string {
  const foRegex = /<foreignObject\b([^>]*)>([\s\S]*?)<\/foreignObject>/gi;
  const replacements: { full: string; textEl: string }[] = [];
  let foMatch;
  while ((foMatch = foRegex.exec(svg)) !== null) {
    const attrs = foMatch[1];
    const inner = foMatch[2];
    const x = parseFloat(attrs.match(/\bx="([^"]*)"/i)?.[1] || "0");
    const y = parseFloat(attrs.match(/\by="([^"]*)"/i)?.[1] || "0");
    const w = parseFloat(attrs.match(/\bwidth="([^"]*)"/i)?.[1] || "100");
    const h = parseFloat(attrs.match(/\bheight="([^"]*)"/i)?.[1] || "30");
    const text = inner.replace(/<[^>]*>/g, "").trim();
    if (!text) continue;
    const tx = x + w / 2;
    const ty = y + h / 2 + 5;
    const fsMatch = inner.match(/font-size\s*:\s*([^;"]+)/i);
    const fontSize = fsMatch ? fsMatch[1].trim().replace(/px|pt|em/, "") : "14";
    replacements.push({
      full: foMatch[0],
      textEl: `<text x="${tx}" y="${ty}" text-anchor="middle" dominant-baseline="central" font-family="sans-serif,Arial,Helvetica" font-size="${fontSize}" fill="#333">${text}</text>`,
    });
  }
  let result = svg;
  for (const r of replacements) {
    result = result.replace(r.full, r.textEl);
  }
  // Strip any remaining <foreignObject> that had no extractable text
  result = result.replace(/<foreignObject[\s\S]*?<\/foreignObject>/gi, "");
  return result;
}

/**
 * Render a mermaid source string to a sandboxed <iframe> containing the SVG.
 * Matches the React reference's renderMermaidToImg function.
 */
export async function renderMermaidToImg(
  source: string
): Promise<{ iframe: string; img: string } | null> {
  const normalized = normalizeMermaidSource(source)
    .replace(/;\s*$/gm, "");

  for (let attempt = 0; attempt < 2; attempt++) {
    const id = `m-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
    ensureMermaidInit();
    try {
      const { svg } = await mermaid.render(id, normalized);
      if (
        /class="error-text"/.test(svg) ||
        /Syntax error in text/.test(svg) ||
        />Syntax error/.test(svg) ||
        />mermaid version/.test(svg) ||
        /Parse error/.test(svg)
      ) {
        console.warn(
          `renderMermaidToImg: mermaid returned error (attempt ${attempt + 1}) for:`,
          source.slice(0, 80)
        );
        if (attempt === 0) continue;
        return null;
      }
      const vbMatch = svg.match(
        /viewBox="(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)"/
      );
      let paddedSvg = svg;
      if (vbMatch) {
        const vx = parseFloat(vbMatch[1]) - 20;
        const vy = parseFloat(vbMatch[2]) - 20;
        const vw = parseFloat(vbMatch[3]) + 40;
        const vh = parseFloat(vbMatch[4]) + 40;
        paddedSvg = svg.replace(
          /viewBox="(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)"/,
          `viewBox="${vx} ${vy} ${vw} ${vh}"`
        );
      }
      const svgW = vbMatch ? parseFloat(vbMatch[3]) + 40 : 1000;
      const svgH = vbMatch ? parseFloat(vbMatch[4]) + 40 : 600;
      if (!svgW || !svgH || svgW < 10 || svgH < 10) {
        console.warn(
          `renderMermaidToImg: bad dimensions (${svgW}x${svgH}), retrying (attempt ${attempt + 1})`
        );
        if (attempt === 0) continue;
        return null;
      }
      const displayW = Math.min(900, svgW);
      const displayH = Math.round((displayW * svgH) / svgW);
      if (!displayW || !displayH || displayW < 10 || displayH < 10) {
        console.warn(
          `renderMermaidToImg: bad display dimensions (${displayW}x${displayH})`
        );
        if (attempt === 0) continue;
        return null;
      }
      /* Don't strip SVG dimensions — let mermaid handle its own layout.
         Instead, scale the entire SVG proportionally via the iframe CSS. */
      const srcdoc = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{margin:0;display:flex;justify-content:center;align-items:flex-start;overflow:auto}svg{max-width:100%;height:auto;display:block;overflow:visible}foreignObject *{overflow-wrap:break-word;word-break:break-all;font-size:13px}</style></head><body>${paddedSvg}</body></html>`;
      const escaped = srcdoc.replace(/"/g, "&quot;");
      const iframeHtml = `<iframe srcdoc="${escaped}" sandbox="allow-same-origin" style="border:none;width:100%;max-width:${displayW}px;height:${displayH}px;display:block;margin:8px 0;overflow:auto;" loading="lazy"></iframe>`;
      const svgBase64 = btoa(unescape(encodeURIComponent(paddedSvg)));
      const imgHtml = `<img src="data:image/svg+xml;base64,${svgBase64}" style="max-width:100%;height:auto;display:block;margin:8px 0;" />`;
      return { iframe: iframeHtml, img: imgHtml };
    } catch (e) {
      console.warn(`renderMermaidToImg failed (attempt ${attempt + 1}):`, e);
      if (attempt === 0) continue;
      return null;
    }
  }
  return null;
}

/**
 * Convert an SVG string to a PNG data URL via canvas.
 * Used for PDF/DOCX export where the backend can't render SVG with custom fonts.
 * Strips <foreignObject> elements and replaces fonts to ensure canvas rendering works.
 */
export async function svgToDataUrlPng(svgStr: string): Promise<string | null> {
  if (typeof document === "undefined") return null;
  try {
    // Replace custom fonts with sans-serif so canvas rendering works
    let sanitized = svgStr
      .replace(/font-family\s*:\s*[^;"]+'Inter Tight'[^;"]*/gi, "font-family: sans-serif")
      .replace(/font-family\s*:\s*'Inter Tight'/gi, "font-family: sans-serif")
      .replace(/'Inter Tight',\s*/gi, "")
      .replace(/"Inter Tight",\s*/gi, "");

    // Convert <foreignObject> text to <text> elements for canvas compatibility
    sanitized = convertForeignObjectToText(sanitized);

    const blob = new Blob([sanitized], { type: "image/svg+xml" });
    const blobUrl = URL.createObjectURL(blob);
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("SVG load failed"));
      image.src = blobUrl;
    });
    const w = img.naturalWidth || 800;
    const h = img.naturalHeight || 400;
    const scale = 2;
    const canvas = document.createElement("canvas");
    canvas.width = w * scale;
    canvas.height = h * scale;
    const ctx = canvas.getContext("2d");
    if (!ctx) { URL.revokeObjectURL(blobUrl); return null; }
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(blobUrl);
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
}

/**
 * Prepare an SVG string for embedding in HTML (PDF/DOCX export).
 * Replaces custom fonts with system fonts so the backend renderer can display text.
 */
export function prepareSvgForExport(svgStr: string): string {
  return svgStr
    .replace(/font-family\s*:\s*[^;"]+'Inter Tight'[^;"]*/gi, "font-family: sans-serif, Arial, Helvetica")
    .replace(/font-family\s*:\s*'Inter Tight'/gi, "font-family: sans-serif, Arial, Helvetica")
    .replace(/'Inter Tight',\s*/gi, "")
    .replace(/"Inter Tight",\s*/gi, "");
}

/** Convert chart-data JSON to a mermaid pie diagram (fallback). */
export function chartDataToMermaidPie(
  chartData: {
    type: string;
    title?: string;
    labels: string[];
    datasets: { label: string; values: number[] }[];
  }
): string {
  const lines: string[] = ["pie"];
  if (chartData.title) lines.push(`    title ${chartData.title}`);
  const values = chartData.datasets[0]?.values || [];
  chartData.labels.forEach((label, i) => {
    const val = values[i] || 0;
    lines.push(`    "${label}" : ${val}`);
  });
  return lines.join("\n");
}

/**
 * Attempt to repair truncated chart-data JSON.
 * The AI often generates incomplete JSON like: {"type": "bar", "labels": [...], "datasets": [{"data": [1, 2,
 * This function closes unclosed brackets/braces to make it parseable.
 */
export function repairTruncatedChartJson(jsonStr: string): string | null {
  let str = jsonStr.trim();
  // Already valid?
  try { JSON.parse(str); return str; } catch { /* continue */ }

  // Remove trailing comma
  str = str.replace(/,\s*$/, "");

  // Track open structures with a stack for correct interleaving
  const stack: string[] = [];
  let inStr = false;
  let escape = false;
  for (const ch of str) {
    if (escape) { escape = false; continue; }
    if (ch === "\\") { escape = true; continue; }
    if (ch === '"') { inStr = !inStr; continue; }
    if (inStr) continue;
    if (ch === "{") stack.push("}");
    else if (ch === "[") stack.push("]");
    else if (ch === "}" || ch === "]") stack.pop();
  }

  // If we're inside a string at the end, close it
  if (inStr) str += '"';

  // Close open structures in reverse order (innermost first)
  // The stack has openers in order, so we reverse to get closers in correct order
  if (stack.length > 0) {
    // Remove trailing comma if present
    str = str.replace(/,\s*$/, "");
    // Append closers in reverse order (innermost first)
    const closers = stack.reverse().join("");
    str += closers;
  }

  // Final cleanup: remove any double commas or trailing commas
  str = str.replace(/,,+/g, ",");
  str = str.replace(/,(\s*[\]}])/g, "$1");

  try {
    const parsed = JSON.parse(str);
    if (parsed && parsed.type && parsed.datasets) return str;
  } catch { /* give up */ }
  return null;
}

/** Render chart-data JSON to a sandboxed iframe with Chart.js, or an img fallback. */
export async function renderChartDataToImg(jsonStr: string): Promise<string | null> {
  try {
    // Try parsing directly, then try repairing truncated JSON
    let chart;
    try {
      chart = JSON.parse(jsonStr);
    } catch {
      const repaired = repairTruncatedChartJson(jsonStr);
      if (repaired) {
        chart = JSON.parse(repaired);
        console.log("🔧 Repaired truncated chart-data JSON");
      } else {
        return null;
      }
    }
    if (!chart || !chart.type || !chart.datasets) return null;

    // Reject placeholder/dummy charts: single-letter labels (A, B, C), trivial title "X", dummy data [1,2,3]
    const labels = chart.labels || [];
    const isDummyLabel = labels.length > 0 && labels.every((l: string) => /^[A-Z]$/.test(l));
    const isDummyTitle = chart.title === "X";
    const isDummyData = chart.datasets.some((ds: Record<string, unknown>) => {
      const data = ds.data as number[];
      return data && data.length > 0 && data.every((v: number) => v <= 5 && v >= 0);
    });
    if (isDummyLabel || isDummyTitle || isDummyData) return null;

    let chartType = chart.type.toLowerCase();
    if (chartType === "xy" || chartType === "xy chart" || chartType === "xychart" || chartType === "scatter") chartType = "scatter";
    if (!["bar", "line", "pie", "doughnut", "scatter", "radar", "polarArea", "bubble"].includes(chartType)) {
      return null;
    }

    if (typeof document === "undefined") return null;

    const hasLabels = labels.length > 0 || chartType === "scatter";

    const datasets = chart.datasets.map((ds: Record<string, unknown>) => ({
      label: (ds.label as string) || "",
      data: ds.data as number[],
      backgroundColor: ds.backgroundColor,
      borderColor: ds.borderColor,
      borderWidth: (ds.borderWidth as number) ?? (chartType === "bar" ? 1 : 2),
      fill: (ds.fill as boolean) ?? (chartType === "line" ? false : undefined),
      tension: chartType === "line" ? 0.3 : undefined,
      pointRadius: chartType === "scatter" ? 5 : chartType === "line" ? 3 : undefined,
      showLine: chartType === "scatter" ? false : undefined,
    }));

    const config: Record<string, unknown> = {
      type: chartType === "doughnut" ? "doughnut" : chartType,
      data: {
        labels: hasLabels ? labels : datasets[0]?.data.map((_: number, i: number) => `#${i + 1}`),
        datasets,
      },
      options: {
        animation: false,
        responsive: false,
        devicePixelRatio: 2,
        plugins: {
          legend: { display: true, position: "bottom" },
          title: chart.title ? { display: true, text: chart.title as string, font: { size: 14 } } : undefined,
        },
      },
    };

    if (chartType !== "pie" && chartType !== "doughnut") {
      (config.options as Record<string, unknown>).scales = {
        x: { title: chart.xLabel ? { display: true, text: chart.xLabel as string } : undefined },
        y: { beginAtZero: true, title: chart.yLabel ? { display: true, text: chart.yLabel as string } : undefined },
      };
    }

    if (!(config.options as Record<string, unknown>).plugins) (config.options as Record<string, unknown>).plugins = {};
    const plugins = (config.options as Record<string, unknown>).plugins as Record<string, unknown>;
    if (!plugins.title) delete plugins.title;

    // Render chart in a sandboxed iframe using Chart.js from CDN
    const chartConfigJson = JSON.stringify(config);
    const chartTitle = (chart.title as string) || chartType + " chart";
    const srcdoc = `<!DOCTYPE html>
<html><head>
<meta charset="utf-8">
<script src="https://cdn.jsdelivr.net/npm/chart.js@4"></script>
<style>body{margin:0;display:flex;justify-content:center;align-items:center;min-height:420px;background:#fff}
canvas{max-width:100%;height:auto}</style>
</head><body>
<canvas id="c" width="700" height="420"></canvas>
<script>
const config=${chartConfigJson};
new Chart(document.getElementById('c'),config);
</script></body></html>`;
    const escaped = srcdoc.replace(/"/g, "&quot;");
    const iframe = `<iframe srcdoc="${escaped}" sandbox="allow-same-origin" style="border:none;width:700px;max-width:100%;height:420px;display:block;margin:8px 0;" loading="lazy"></iframe>`;

    // Also render to canvas for PNG fallback (used in export / modal)
    const canvas = document.createElement("canvas");
    canvas.width = 700;
    canvas.height = 420;
    const ctx = canvas.getContext("2d");
    let imgHtml: string | null = null;
    if (ctx) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const chartInstance = new Chart(ctx, config as any);
      await new Promise((r) => setTimeout(r, 100));
      imgHtml = `<img src="${canvas.toDataURL("image/png")}" alt="${chartTitle}" style="max-width:100%;height:auto;" />`;
      chartInstance.destroy();
    }

    return JSON.stringify({ iframe, img: imgHtml || "" });
  } catch (e) {
    console.warn("renderChartDataToImg failed:", e);
    return null;
  }
}

/**
 * Render a mermaid source string to a PNG data URL.
 *
 * Flow:
 *  1. Call mermaid.render() to get a clean, properly-styled SVG string
 *     (correct shapes, fills, strokes — all inlined in a <style> block)
 *  2. Read viewBox for true intrinsic dimensions (not layout-constrained)
 *  3. Stamp explicit width/height + padded viewBox on the SVG element
 *  4. Encode as a data:image/svg+xml URL and load into an <img>
 *     (browser image renderer handles <foreignObject> correctly here,
 *      unlike canvas drawImage from a blob URL in some browsers)
 *  5. Draw onto a 3× canvas for high-res output → PNG data URL
 *
 * @param source  Raw mermaid diagram source (without the ```mermaid fence)
 * @param idSuffix  Optional suffix to make the mermaid render id unique
 * @returns PNG data URL string, or null on failure
 */
export async function mermaidSourceToPng(source: string, idSuffix: string | number = Date.now()): Promise<string | null> {
  if (typeof document === "undefined") return null;
  try {
    mermaid.initialize({
      startOnLoad:   false,
      theme:         "default",
      securityLevel: "loose",
      fontFamily:    "Arial, Helvetica, sans-serif",
    });

    const id = `mermaid-png-${idSuffix}`;
    const normalized = normalizeMermaidSource(source)
      .replace(/^flowchart\b/m, "graph")
      .replace(/;\s*$/gm, "");

    const { svg } = await mermaid.render(id, normalized);

    // Parse viewBox for true content dimensions
    const vbMatch = svg.match(/viewBox="([^"]*)"/);
    let vbX = 0, vbY = 0, vbW = 800, vbH = 400;
    if (vbMatch) {
      const p = vbMatch[1].trim().split(/[\s,]+/).map(Number);
      if (p.length === 4 && p[2] > 10 && p[3] > 10) [vbX, vbY, vbW, vbH] = p;
    }

    const PAD = 20;
    const W   = vbW + PAD * 2;
    const H   = vbH + PAD * 2;

    // Stamp explicit size and shift viewBox to add padding
    const sized = svg.replace(/<svg\b[^>]*>/, (tag) =>
      tag
        .replace(/\s*(width|height|style)="[^"]*"/gi, "")
        .replace(/viewBox="[^"]*"/, `viewBox="${vbX - PAD} ${vbY - PAD} ${W} ${H}"`)
        + ` width="${W}" height="${H}"`
    );

    // Use encodeURIComponent data URL — avoids blob URL taint issues
    const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(sized)}`;

    const SCALE = 3;
    return await new Promise<string | null>((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width  = W * SCALE;
        canvas.height = H * SCALE;
        const ctx = canvas.getContext("2d");
        if (!ctx) { resolve(null); return; }
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = () => resolve(null);
      img.src = dataUrl;
    });
  } catch (e) {
    console.warn("mermaidSourceToPng failed:", e);
    return null;
  }
}
